import { useQuery } from '@tanstack/react-query';
import {
  getComplaints,
  type Complaint,
  type ComplaintsListParams,
  type ComplaintStats,
} from '../api';

/** How many records to pull per backend request while loading the full set. */
const FETCH_PAGE_SIZE = 100;
/** Safety cap so a runaway dataset can't loop forever. */
const MAX_PAGES = 500;

export interface PaginatedComplaintsResult {
  complaints: Complaint[];
  stats?: ComplaintStats;
}

/**
 * Loads the complete filtered complaint set by walking the cursor-based API,
 * so the page can render classic numbered pagination (jump to any page) with an
 * exact total. Stats are requested only on the first page.
 *
 * The backend remains cursor-based (DynamoDB has no offset); we materialize the
 * full set here and slice it client-side. This mirrors the existing full-scan
 * the stats endpoint already performs per request.
 */
export function usePaginatedComplaintsWithStats(
  params?: Omit<ComplaintsListParams, 'cursor' | 'limit'>,
  enabled = true
) {
  return useQuery<PaginatedComplaintsResult, Error>({
    queryKey: ['complaints', 'paginated', params],
    queryFn: async (): Promise<PaginatedComplaintsResult> => {
      const complaints: Complaint[] = [];
      let stats: ComplaintStats | undefined;
      let cursor: string | undefined;
      let pages = 0;

      do {
        const res = await getComplaints({
          ...params,
          limit: FETCH_PAGE_SIZE,
          cursor,
          includeStats: !cursor,
        });
        if (!cursor) stats = res.stats;
        complaints.push(...res.data);
        cursor = res.nextCursor;
        pages += 1;
      } while (cursor && pages < MAX_PAGES);

      return { complaints, stats };
    },
    enabled,
    staleTime: 30 * 1000,
  });
}
